import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { loadTasks } from '@/utils/storage';
import { Task } from '@/types/task';

interface TaskContextType {
  tasks: Task[];
  completedTasks: Task[];
  activeTasks: Task[];
  loading: boolean;
  refreshTasks: () => Promise<void>;
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export function TaskProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshTasks = async () => {
    try {
      setLoading(true);
      const allTasks = await loadTasks();
      setTasks(allTasks);
      console.log(`[TaskContext] Loaded ${allTasks.length} tasks`);
    } catch (error) {
      console.error('[TaskContext] Error loading tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshTasks();

    const handleTasksUpdated = (event: Event) => {
      console.log('[TaskContext] Tasks updated event received');
      refreshTasks();
    };

    window.addEventListener('tasks-updated', handleTasksUpdated);
    
    return () => {
      window.removeEventListener('tasks-updated', handleTasksUpdated);
    };
  }, []);

  const completedTasks = tasks.filter(task => task.completed);
  const activeTasks = tasks.filter(task => !task.completed);

  const value = {
    tasks,
    completedTasks,
    activeTasks,
    loading,
    refreshTasks,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}

export const useTask = () => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTask must be used within a TaskProvider');
  }
  return context;
};